import type { FleetTaskPriority, FleetTaskSource, FleetTaskStatus } from '@mt/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { FLEET_KEY } from './api';

/** DEC-430: việc giao tài xế (bảo dưỡng, rửa xe, giấy tờ tự sinh; nhắc việc khẩn quản lý giao). */
export interface FleetTask {
  id: number;
  source: FleetTaskSource;
  sourceLabel: string;
  vehicleId: number | null;
  plateNo: string | null;
  driverId: number | null;
  assigneeName: string | null;
  maintenanceItemId: number | null;
  isWash: boolean;
  docType: string | null;
  title: string;
  detail: string | null;
  priority: FleetTaskPriority;
  dueOn: string | null;
  dueAt: string | null;
  overdue: boolean;
  requirePhoto: boolean;
  requireKm: boolean;
  status: FleetTaskStatus;
  statusLabel: string;
  acceptedAt: string | null;
  acceptedByName: string | null;
  doneAt: string | null;
  doneByName: string | null;
  doneNote: string | null;
  doneKm: number | null;
  donePhotoId: number | null;
  closedAt: string | null;
  closedByName: string | null;
  closeNote: string | null;
  createdById: number | null;
  createdByName: string | null;
  createdAt: string;
  version: number;
}
export type TaskView = 'active' | 'done' | 'closed';
export interface TaskList {
  today: string;
  items: FleetTask[];
  counts: { active: number; urgent: number; done: number; overdue: number };
}

const TASK_KEY = [...FLEET_KEY, 'tasks'];
export const useFleetTasks = (view: TaskView, enabled = true) =>
  useQuery({ queryKey: [...TASK_KEY, view], queryFn: () => api.get<TaskList>('/fleet/tasks', { view }), enabled, refetchInterval: 60_000 });
export const useMyTasks = (enabled = true) =>
  useQuery({ queryKey: [...TASK_KEY, 'mine'], queryFn: () => api.get<{ today: string; items: FleetTask[] }>('/fleet/tasks/mine'), enabled, retry: false, refetchInterval: 60_000 });

/**
 * Anh Tiến 01/11/2026: tab "Việc giao" riêng ở màn tài xế, gắn nhãn đỏ "Gấp" (số việc Khẩn hoặc Quá hạn chưa xong).
 * todo = việc còn phải làm (chưa nhận / đang làm); gap = trong đó việc Khẩn hoặc Quá hạn.
 */
export function myTaskCounts(items: Pick<FleetTask, 'status' | 'priority' | 'overdue'>[]) {
  const todo = items.filter((t) => t.status === 'open' || t.status === 'accepted');
  return { todo: todo.length, gap: todo.filter((t) => t.priority === 'urgent' || t.overdue).length };
}

/** Gửi 1 thao tác trên việc, báo kết quả, tải lại danh sách (cả lịch nhắc vì hạng mục bảo dưỡng có thể đổi). */
export function useTaskAction<V>(fn: (v: V) => Promise<unknown>, ok: string, onDone?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      toast.success(ok);
      void qc.invalidateQueries({ queryKey: FLEET_KEY });
      onDone?.();
    },
    onError: (e) => toast.error((e as Error).message),
  });
}

/** "trước 10:30 22/10" / "hạn 25/10/2026" */
export function taskDueText(t: Pick<FleetTask, 'dueAt' | 'dueOn'>) {
  if (t.dueAt) return `trước ${new Date(t.dueAt).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}`;
  if (t.dueOn) return `hạn ${t.dueOn.slice(8, 10)}/${t.dueOn.slice(5, 7)}/${t.dueOn.slice(0, 4)}`;
  return '';
}

export const TASK_STATUS_VARIANT: Record<FleetTaskStatus, 'warning' | 'info' | 'success' | 'neutral' | 'error'> = {
  open: 'warning',
  accepted: 'info',
  done: 'info',
  closed: 'success',
  cancelled: 'neutral',
};
